import React from 'react';
import {
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { Payor, Plan } from "@finpay-estimation/estimation-types";
import { isFacilityPlanIdOnFacility, rematchRateAsPayorPlanToFacility } from '../../../../utils/payor-plan-utils';

interface RateAsSelectorProps {
    facilityPayersState?: FacilityPayerOption[] | null;
    payersMap?: Map<number, { payorName?: string; payorPlans?: { payorPlanId: number; planName?: string }[] }>;
    /** Called when user selects a different option; parent typically syncs IN/OUT tab and calls loadAndSetRateAsPayorPlan to update Redux */
    onRateAsPayorPlanChange?: (facilityPlanId: number) => void;
    /** From Redux; used when VOB has no rateAsPayorPlan */
    rateAsPayorPlan?: {
        payor: Payor;
        plan: Plan | null;
    } | null;
    /** From current VOB (API or vob.payor + vob.plan); when present, used to traverse options and display */
    vobRateAsPayorPlan?: {
        payor: Payor;
        plan: Plan | null;
    } | null;
    /** When set, dropdown shows this instead of rateAsPayorPlan (e.g. user selected for display only, Redux unchanged) */
    selectedFacilityPlanId?: number;
    /** Form selected facility id; when !== vobFacilityId, use Redux rateAsPayorPlan so dropdown has a valid value */
    selectedFacility?: number | '';
    /** VOB's facility id */
    vobFacilityId?: number;
}

interface FacilityPayerOption {
    cfgPayorId: number;
    facilityPayorName?: string;
    inNetwork: boolean;
    facilityPayorPlans?: { facilityPayorPlanId: number; cfgPayorPlanId: number; facilityPayorPlanName: string }[];
}

export const RateAsSelector: React.FC<RateAsSelectorProps> = ({
    facilityPayersState,
    payersMap,
    onRateAsPayorPlanChange,
    rateAsPayorPlan,
    vobRateAsPayorPlan,
    selectedFacilityPlanId,
    selectedFacility,
    vobFacilityId,
}) => {
    // When user changed facility (form facility !== VOB facility), use Redux rateAsPayorPlan so dropdown value exists in new facility's options.
    const facilityChanged = selectedFacility != null && selectedFacility !== '' && vobFacilityId != null && Number(selectedFacility) !== Number(vobFacilityId);

    // Prefer "same as before change" when it exists in the new facility: match vobRateAsPayorPlan by cfg payor/plan id in facilityPayersState.
    const preferredMatchBeforeChange = (() => {
        if (!facilityChanged || !vobRateAsPayorPlan?.payor?.payorId || !vobRateAsPayorPlan?.plan?.payorPlanId || !facilityPayersState?.length) return null;
        const vobPayorId = Number(vobRateAsPayorPlan.payor.payorId);
        const vobPlanId = Number(vobRateAsPayorPlan.plan.payorPlanId);
        for (const payer of facilityPayersState) {
            if (Number(payer.cfgPayorId) !== vobPayorId) continue;
            const plan = (payer.facilityPayorPlans ?? []).find((p: { cfgPayorPlanId?: number }) => Number(p.cfgPayorPlanId) === vobPlanId);
            if (plan) return { payer, plan };
        }
        return null;
    })();

    const rateAsFromPreferredMatch =
        facilityChanged && preferredMatchBeforeChange
            ? {
                payor: {
                    payorId: preferredMatchBeforeChange.payer.cfgPayorId,
                    payorName: payersMap?.get(preferredMatchBeforeChange.payer.cfgPayorId)?.payorName,
                    facilityPayorName: preferredMatchBeforeChange.payer.facilityPayorName,
                    inNetwork: preferredMatchBeforeChange.payer.inNetwork
                } as Payor,
                plan: {
                    payorPlanId: preferredMatchBeforeChange.plan.cfgPayorPlanId,
                    facilityPlanId: preferredMatchBeforeChange.plan.facilityPayorPlanId,
                    planName: payersMap?.get(preferredMatchBeforeChange.payer.cfgPayorId)?.payorPlans?.find((p: { payorPlanId: number }) => Number(p.payorPlanId) === preferredMatchBeforeChange.plan.cfgPayorPlanId)?.planName,
                    facilityPlanName: preferredMatchBeforeChange.plan.facilityPayorPlanName
                } as Plan
            }
            : null;

    const displayRateAs = (() => {
        if (rateAsPayorPlan?.plan?.facilityPlanId != null && isFacilityPlanIdOnFacility(facilityPayersState, rateAsPayorPlan.plan.facilityPlanId)) {
            return rateAsPayorPlan;
        }
        if (facilityChanged && rateAsFromPreferredMatch) {
            return rateAsFromPreferredMatch;
        }
        if (rateAsPayorPlan && facilityPayersState?.length) {
            const rematched = rematchRateAsPayorPlanToFacility(rateAsPayorPlan, facilityPayersState, payersMap);
            if (rematched) return rematched;
        }
        return rateAsPayorPlan ?? rateAsFromPreferredMatch ?? vobRateAsPayorPlan;
    })();

    // Align select value with displayRateAs (Redux first); preferredMatch only steers facility id when Redux has no rate-as after facility change.
    // For historical VOBs, displayRateAs may be vob data; resolve facilityPayorPlanId from facilityPayersState.
    const rateAsSelectValue = (() => {
        if (selectedFacilityPlanId != null) {
            return isFacilityPlanIdOnFacility(facilityPayersState, selectedFacilityPlanId)
                ? selectedFacilityPlanId
                : '';
        }
        if (facilityChanged && preferredMatchBeforeChange && rateAsPayorPlan == null) {
            return preferredMatchBeforeChange.plan.facilityPayorPlanId;
        }
        const source = displayRateAs;
        if (!source?.plan || !facilityPayersState?.length) {
            return source?.plan?.facilityPlanId ?? (source?.plan as { facilityPayorPlanId?: number })?.facilityPayorPlanId ?? '';
        }
        let targetId = source.plan.facilityPlanId ?? (source.plan as { facilityPayorPlanId?: number }).facilityPayorPlanId;
        if (targetId == null && source.payor?.payorId != null && source.plan?.payorPlanId != null) {
            const payorId = Number(source.payor.payorId);
            const planId = Number(source.plan.payorPlanId);
            for (const payer of facilityPayersState) {
                if (Number(payer.cfgPayorId) !== payorId) continue;
                const plan = (payer.facilityPayorPlans ?? []).find((p: { cfgPayorPlanId?: number }) => Number(p.cfgPayorPlanId) === planId);
                if (plan?.facilityPayorPlanId != null) {
                    targetId = plan.facilityPayorPlanId;
                    break;
                }
            }
        }
        if (targetId == null) return '';
        const numTarget = Number(targetId);
        for (const payer of facilityPayersState) {
            for (const plan of payer.facilityPayorPlans ?? []) {
                if (Number(plan.facilityPayorPlanId) === numTarget) return plan.facilityPayorPlanId;
            }
        }
        return '';
    })();

    return (
        <>
            {displayRateAs && (facilityPayersState?.length ? (
                <FormControl size="small" sx={{ minWidth: 280, mb: 3 }} test-id="vob-rate-as-payor-plan-select">
                    <InputLabel id="vob-rate-as-label">Rate as</InputLabel>
                    <Select
                        labelId="vob-rate-as-label"
                        id="vob-rate-as-payor-plan-select"
                        value={rateAsSelectValue}
                        onChange={(e) => onRateAsPayorPlanChange?.(Number(e.target.value))}
                        label="Rate as"
                        sx={{ fontWeight: 'normal', color: 'text.secondary' }}
                    >
                        {facilityPayersState
                            .filter((payer) => {
                                const name = (payer.facilityPayorName || payersMap?.get(payer.cfgPayorId)?.payorName || '').toLowerCase();
                                return !name.includes('self-pay') && !name.includes('self pay');
                            })
                            .flatMap((payer) =>
                                (payer.facilityPayorPlans || []).map((plan) => {
                                    const payorName = payer.facilityPayorName || payersMap?.get(payer.cfgPayorId)?.payorName || '';
                                    const planName = plan.facilityPayorPlanName || payersMap?.get(payer.cfgPayorId)?.payorPlans?.find((p) => Number(p.payorPlanId) === Number(plan.cfgPayorPlanId))?.planName || '';
                                    const label = `${payorName}${planName ? `/${planName}` : ''} - ${payer.inNetwork ? 'In Network' : 'Out of Network'}`;
                                    return (
                                        <MenuItem key={plan.facilityPayorPlanId} value={plan.facilityPayorPlanId} test-id={`vob-rate-as-option-${plan.facilityPayorPlanId}`}>
                                            {label}
                                        </MenuItem>
                                    );
                                })
                            )}
                    </Select>
                </FormControl>
            ) : (
                <Typography component="div" variant="body2" color="textSecondary" sx={{ fontWeight: 'normal', mb: 3 }} test-id="vob-levels-of-care-rate-as">
                    Rate as: ({displayRateAs?.payor?.facilityPayorName || displayRateAs?.payor?.payorName}/{displayRateAs?.plan?.planName}) - {displayRateAs?.payor?.inNetwork ? 'In Network' : 'Out of Network'}
                </Typography>
            ))}
        </>
    )
}